import { LitElement, html, css, unsafeCSS } from 'lit';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Pencil, Trash2, Plus, Search, Folder, Upload, FileUp, X, Info, ClipboardList } from 'lucide';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { formatDateTime } from '../utils/datetime.js';
import { betaFeatureAllowed, COORDINATE_SYSTEMS, HEIGHT_DATUMS, DEFAULT_COORDINATE_SYSTEM, DEFAULT_HEIGHT_DATUM, SOIL_STANDARDS, SELECTABLE_SOIL_STANDARDS, DEFAULT_SOIL_STANDARD, normalizeSoilStandard, nen6693, DEFAULT_TIMEZONE } from '@mv/models';
import styles from '../styles/project-dashboard.css?inline';
import './mv-table.js';

import { lucideIcon } from '../icon.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { projectVelden } from '../utils/project-velden.js';
import { tijdzoneVeld } from '../utils/tijdzone-veld.js';
import { fieldOrderDocumentToText, FIELD_ORDER_ACCEPT } from '../utils/veldwerkopdracht/document-tekst.js';
import { kanScanZijn, teWeinigTekst, tekstUitScan, SCAN_VRAAG } from '../utils/veldwerkopdracht/scan-ocr.js';
import { controleerScan } from '../utils/veldwerkopdracht/scan-controle.js';

const GRAIN_SIZE_TIP =
  'Zet de mogelijkheid aan om bij een laag lutum, leem, grind, silt, organische stof\n'
  + 'en M50 op te geven. Vul je ze in, dan volgen de grondsoort, de bijmengingen en de\n'
  + 'mediaan daaruit; vul je ze niet in, dan kies je die zelf zoals altijd.\n'
  + 'Laat dit uit als jullie in het veld schatten in plaats van meten.';

const SOIL_STANDARD_TIP =
  'Dit is de standaard waarnaar we het gesproken boorprofiel vertalen.\n'
  + 'Je kunt per specifieke boring alsnog een alternatieve standaard opgeven.';

/**
 * Uitleg-icoontje bij een label: de tekst verschijnt zodra je erover zweeft.
 *
 * Niet als klik-uitklapper (dan moet je raden dat er iets achter zit, en verschuift het
 * formulier onder je handen) en niet als `title` (die wacht een seconde, en dan heb je je
 * vraag al opgegeven). Zelfde vorm als bij de klantcode op een labopdracht, zodat een ⓘ
 * overal hetzelfde doet.
 */
function infoTip(text) {
  return html`<span data-tip=${text} @mousemove=${tipMove} @mouseleave=${tipHide}
    style="display:inline-flex; vertical-align:-2px; color:var(--mv-text-sub,#6c757d); cursor:help;"
    >${unsafeSVG(lucideIcon(Info, 13))}</span>`;
}

/**
 * Project dashboard showing all projects with creation and basic management
 */
export class ProjectDashboard extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;

  static properties = {
    projects: { type: Array },
    creating: { type: Boolean },
    projectNameValue: { type: String },
    projectCodeValue: { type: String },
    coordinateSystemValue: { type: String },
    heightDatumValue: { type: String },
    soilStandardValue: { type: String },
    /** Korrelverdeling invullen bij een laag? Zie GRAIN_SIZE_TIP. */
    grainSizeInputValue: { type: Boolean },
    timezoneValue: { type: String },
    werksoortValue: { type: String },
    templateValue: { type: String },
    templates: { type: Array },
    _werksoortInfoOpen: { state: true },
    _step: { state: true },
    // Hoe je dit project aanmaakt: 'handmatig' of 'upload'. Null zolang je nog niet gekozen
    // hebt — dan staat de keuzestap open en weten we nog niet hoeveel stappen er volgen.
    _method: { state: true },
    _uploadOpen: { state: true },
    // Slepen-en-neerzetten: het vlak kleurt op zodra je eroverheen zweeft, het gekozen
    // bestand staat in beeld, en een fout blijft staan tot je iets anders kiest.
    _dragOver: { state: true },
    _uploadBestand: { state: true },
    _uploadFout: { state: true },
    // Wat de import opleverde: per regel { level: 'ok'|'warning'|'error', text }.
    _importLog: { state: true },
    _importBezig: { state: true },
    _templateInitial: { state: true },    // sjabloon bij openen (voor "gewijzigd?"-waarschuwing)
    showCreateModal: { type: Boolean },
    projectToEdit: { type: Object },
    createError: { type: String },
    searchQuery: { type: String },
    // Licentie van het actieve bedrijf: 'geovoice' | 'geodesk'. Bepaalt of de kolom
    // Labopdrachten er staat. Blijft null tot hij geladen is, en dan tonen we de kolom niet:
    // een kolom die verschijnt en weer verdwijnt is storender dan eentje die iets later komt.
    _companyLicense: { state: true },
    _vooronderzoekOk: { state: true },  // bètafunctie Vooronderzoek: 'Vanuit veldwerkopdracht' mag
    _voBestand: { state: true },        // gekozen veldwerkopdracht
    _voBezig: { state: true },          // tekst bij het aanmaken ('Formulier lezen…' enz.)
  };

  constructor() {
    super();
    this.projects = [];
    this.searchQuery = '';
    this.showCreateModal = false;
    this.newProject = { name: '', code: '' };
    this.error = null;
    this.isCreating = false;
    this.projectId = null;
    this.parentProjectId = '';
    this.projectNameValue = '';
    this.projectCodeValue = '';
    this.templateValue = '';
    this.templates = [];
    this._companyLicense = null;
    // Het aanmaakvenster begint bij de methodekeuze; nog niets gekozen, dus nog geen stappen.
    this._method = null;
    this._uploadOpen = false;
    this._dragOver = false;
    this._uploadBestand = null;
    this._uploadFout = null;
    this._importLog = [];
    this._importBezig = false;
    /*
     * Twee rechten, en ze verschillen.
     *
     * Aanpassen mag iedereen die bij het bedrijf hoort: een lid gebruikt en digitaliseert
     * projecten, en dan hoort het corrigeren van een naam of locatie erbij. Verwijderen niet --
     * dat is onomkeerbaar en gaat over het werk van een heel bedrijf.
     *
     * Dit scherm keek naar de ACCOUNT-rol (`user.role === 'admin'`) en verstopte daarmee de hele
     * actiekolom voor iedereen die geen accountbeheerder was, ongeacht zijn rol binnen het
     * bedrijf. De rest van de app kijkt al naar de bedrijfsrol; dit was de laatste plek die dat
     * niet deed.
     */
    const companyRole = auth.getActiveCompany()?.role;
    this.canDeleteProjects = auth.isSystemAdmin() || companyRole === 'owner' || companyRole === 'manager';
  }

  connectedCallback() {
    super.connectedCallback();
    this._onEsc = (e) => { if (e.key === 'Escape' && this.showCreateModal) this.closeCreateModal(); };
    document.addEventListener('keydown', this._onEsc);
    this._loadTemplates();
    this._loadLicense();
  }

  /** Licentie van het actieve bedrijf; faalt hij, dan tonen we de kolom Labopdrachten niet. */
  async _loadLicense() {
    try {
      const profile = await api.getCompanyProfile();
      this._companyLicense = profile?.license || 'geovoice';
      this._vooronderzoekOk = betaFeatureAllowed({ license: profile?.license, betaFeatures: profile?.betaFeatures }, 'vooronderzoek');
    } catch { this._companyLicense = 'geovoice'; this._vooronderzoekOk = false; }
  }

  async _loadTemplates() {
    try { this.templates = await api.getTemplates(); } catch { this.templates = []; }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onEsc);
  }

  updated(changedProperties) {
    if (changedProperties.has('parentProjectId') && this.parentProjectId && !this.showCreateModal) {
      this.toggleCreateModal();
    }
    if (changedProperties.has('projectToEdit') && this.projectToEdit && !this.showCreateModal) {
      this.toggleCreateModal(this.projectToEdit);
    }
  }

  toggleCreateModal(project = null) {
    if (project && project.id) {
      this.projectId = project.id;
      this.projectNameValue = project.project_name || project.name || '';
      this.projectCodeValue = project.project_code || '';
      this.parentProjectId = project.parent_id || '';
      this.instructionsValue = project.instructions || '';
    } else if (project && project.parent_id) {
      // New sub-project with pre-selected parent
      this.projectId = null;
      this.projectNameValue = '';
      this.projectCodeValue = this._generateProjectCode();
      this.parentProjectId = project.parent_id;
      this.instructionsValue = '';
    } else {
      this.projectId = null;
      this.projectNameValue = '';
      this.projectCodeValue = this._generateProjectCode();
      this.parentProjectId = this.parentProjectId || ''; // Keep preset if exists
      this.instructionsValue = '';
    }
    this.coordinateSystemValue = (project && project.id ? project.coordinate_system : null) || DEFAULT_COORDINATE_SYSTEM;
    this.heightDatumValue = (project && project.id ? project.height_datum : null) || DEFAULT_HEIGHT_DATUM;
    // Boorprofiel-standaard; nieuwe projecten default naar de verplichte NEN 6693.
    this.soilStandardValue = normalizeSoilStandard(project && project.id ? project.soil_standard : DEFAULT_SOIL_STANDARD);
    // Standaard uit; alleen een bestaand project dat hem aan had heeft hem aan.
    this.grainSizeInputValue = !!(project && project.grain_size_input);
    this.timezoneValue = (project && project.id ? project.timezone : null) || DEFAULT_TIMEZONE;
    this.werksoortValue = (project && project.id ? project.werksoort : null) || '';
    this.templateValue = (project && project.id ? project.template_id : null) || '';
    this._templateInitial = this.templateValue;
    this._werksoortInfoOpen = false;
    this._step = 1;
    this.createError = null;
    this.showCreateModal = true;
  }

  /**
   * Genereer een voorgestelde projectcode (PRJ-<jaar>-<volgnummer>), gebaseerd op de
   * hoogste bestaande code van dit jaar. De gebruiker ziet deze waarde vooraf ingevuld
   * en kan 'm aanpassen; laat 'ie het veld leeg, dan wordt geen code opgeslagen.
   */
  _generateProjectCode() {
    const year = new Date().getFullYear();
    const prefix = `PRJ-${year}-`;
    let max = 0;
    for (const p of this.projects || []) {
      const code = p.project_code || '';
      if (code.startsWith(prefix)) {
        const n = parseInt(code.slice(prefix.length), 10);
        if (!Number.isNaN(n) && n > max) max = n;
      }
    }
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.createError = null;
    // Terug naar de keuzestap: anders begint de volgende keer aanmaken bij de methode die je
    // de vorige keer koos, terwijl je dat misschien juist anders wilt.
    this._step = 1;
    this._method = null;
    this._uploadOpen = false;
    this._dragOver = false;
    this._uploadBestand = null;
    this._uploadFout = null;
    this._voBestand = null;
    this._voBezig = null;
    this._importLog = [];
    this._importBezig = false;
    this.projectId = null;
    this.parentProjectId = '';
    this.projectNameValue = '';
    this.projectCodeValue = '';
    this.instructionsValue = '';
    this.dispatchEvent(new CustomEvent('modal-closed'));
  }

  /**
   * DB-tijdstempels zijn UTC zonder tijdzone ("YYYY-MM-DD HH:MM:SS"). Direct aan `new Date()`
   * voeren laat JS ze als LOKALE tijd lezen — dat scheelt in Nederland 1 à 2 uur. De gedeelde
   * helper parseert ze als UTC en toont ze in de tijdzone van de kijker.
   */
  formatDate(dateString) {
    return dateString ? formatDateTime(dateString) : 'Onbekend';
  }

  handleSearchInput(event) {
    this.searchQuery = event.target.value.toLowerCase();
  }

  get filteredProjects() {
    let base = [...this.projects];
    const query = (this.searchQuery || '').toLowerCase();

    if (query) {
      base = base.filter(p => {
        const name = (p.project_name || p.name || '').toLowerCase();
        const code = (p.project_code || '').toLowerCase();
        const id = (p.id || '').toLowerCase();
        return name.includes(query) || code.includes(query) || id.includes(query);
      });
    }

    // Zoeken: een platte lijst. Anders hoofdprojecten met hun deelprojecten eronder.
    if (query) return base;

    const parents = base.filter(p => !p.parent_id);
    const children = base.filter(p => p.parent_id);
    
    const result = [];
    parents.forEach(p => {
      const myChildren = children.filter(c => String(c.parent_id).toLowerCase() === String(p.id).toLowerCase());
      // A parent shows ALL borings of the meting: its own + every subproject's —
      // matching the project detail view, which aggregates parent + deelprojecten.
      const childBorings = myChildren.reduce((sum, c) => sum + (Number(c.boring_count) || 0), 0);
      // Labopdrachten tellen op dezelfde manier op als de boringen: zou de ene kolom het hele
      // project omvatten en de andere alleen het hoofdproject, dan spreken twee getallen op
      // dezelfde regel elkaar tegen.
      const childLabOrders = myChildren.reduce((sum, c) => sum + (Number(c.lab_order_count) || 0), 0);
      result.push({
        ...p,
        isChild: false,
        boring_count: (Number(p.boring_count) || 0) + childBorings,
        lab_order_count: (Number(p.lab_order_count) || 0) + childLabOrders,
      });
      myChildren.forEach(c => {
        result.push({ ...c, isChild: true });
      });
    });

    // Handle orphans (children whose parent is not in the list)
    const activeIds = new Set(parents.map(p => String(p.id).toLowerCase()));
    const orphans = children.filter(c => !activeIds.has(String(c.parent_id).toLowerCase()));
    orphans.forEach(o => result.push({ ...o, isChild: false }));

    return result;
  }

  get tableColumns() {
    const cols = [
      {
        id: 'name',
        title: 'Projectnaam',
        render: (row) => html`
          <div style="display:flex; align-items:center; gap:8px; padding-left: ${row.isChild ? '24px' : '0'}">
            ${row.isChild ? html`<span style="color:var(--mv-text-muted); font-size: 14px;">┕</span>` : ''}
            <strong>${row.project_name || row.name || row.id}</strong>
          </div>
        `
      },
      {
        id: 'code',
        title: 'Projectcode',
        render: (row) => row.project_code ? row.project_code : html`<span style="color:var(--mv-text-muted)">-</span>`
      },
      {
        id: 'standard',
        title: 'Standaard',
        render: (row) => {
          const std = normalizeSoilStandard(row.soil_standard);
          const is5104 = std === 'nen5104';
          const text = is5104 ? '#6c757d' : '#2e9e5b';
          const border = is5104 ? 'rgba(108,117,125,0.35)' : 'rgba(46,158,91,0.35)';
          const bg = is5104 ? 'rgba(108,117,125,0.12)' : 'rgba(46,158,91,0.12)';
          return html`<span style="display:inline-block; padding:2px 8px; border-radius:5px; border:1px solid ${border}; background:${bg}; color:${text}; font-size:0.72rem; font-weight:800; letter-spacing:0.02em; white-space:nowrap;">${SOIL_STANDARDS[std].short}</span>`;
        }
      },
      {
        id: 'boring_count',
        title: 'Boringen',
        render: (row) => html`<span style="color:var(--mv-text-muted); font-weight: 500">${row.boring_count || 0}</span>`
      },
      // Alleen met een GeoDesk-licentie: zonder die licentie bestaan er geen labopdrachten en
      // zou de kolom een lege kolom nullen zijn die vragen oproept.
      ...(this._companyLicense === 'geodesk' ? [{
        id: 'lab_order_count',
        title: 'Labopdrachten',
        render: (row) => html`<span style="color:var(--mv-text-muted); font-weight: 500">${row.lab_order_count || 0}</span>`
      }] : []),
    ];

    cols.push({
      id: 'actions', title: '', width: '80px',
      render: (row) => html`
        <button class="action-btn" @click=${(e) => { e.stopPropagation(); this.toggleCreateModal(row); }} title="Bewerken">
          ${unsafeSVG(lucideIcon(Pencil, 15))}
        </button>
        ${this.canDeleteProjects ? html`
          <button class="action-btn delete" @click=${(e) => { e.stopPropagation(); this.deleteProject(row); }} title="Verwijderen">
            ${unsafeSVG(lucideIcon(Trash2, 15))}
          </button>` : ''}
      `
    });

    return cols;
  }

  handleNameInput(event) {
    this.projectNameValue = event.target.value;
    if (this.createError && this.projectNameValue.trim()) {
      this.createError = null; // Clear error when user starts typing
    }
  }

  handleCodeInput(event) {
    this.projectCodeValue = event.target.value;
  }

  // --- Stepper-navigatie -----------------------------------------------------
  /**
   * De stappen van het aanmaakvenster. 'Methode' staat vooraan: eerst kies je hóe je dit
   * project maakt, want die keuze bepaalt wat er daarna komt. Kies je uploaden, dan is er na
   * het bestand niets meer te vullen -- de gegevens komen uit de export.
   */
  static get STEP_TITLES() {
    return ['Methode', 'Basisgegevens', 'Standaarden & systemen', 'Onderzoek'];
  }

  /**
   * De stappen bij uploaden: kiezen, het bestand, en wat de import ervan maakte.
   *
   * Die derde stap is geen formaliteit: bij een import van tientallen boringen wil je zien wát
   * er binnenkwam en wat er is overgeslagen, vóórdat het project bestaat.
   */
  static get UPLOAD_STEP_TITLES() {
    return ['Methode', 'Bestand', 'Resultaat'];
  }

  /** Vanuit een veldwerkopdracht: kiezen en het formulier; de rest leest GeoVoice eruit. */
  static get VELDWERK_STEP_TITLES() {
    return ['Methode', 'Veldwerkopdracht'];
  }

  get _selectedTemplate() {
    return (this.templates || []).find(t => t.id === this.templateValue) || null;
  }

  /** Alleen beheerders/eigenaren (of sysadmin) mogen sjablonen aanmaken. */
  get _canManageTemplates() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  get _lastStep() {
    return this._visibleStepTitles.length;
  }

  get _visibleStepTitles() {
    // Zolang je niets hebt gekozen, staat er één stap. Welke stappen volgen hángt van die
    // keuze af, en vier stappen tonen die daarna twee kunnen worden is een belofte die je
    // meteen weer inslikt.
    if (!this._method) return [ProjectDashboard.STEP_TITLES[0]];
    if (this._method === 'veldwerkopdracht') return ProjectDashboard.VELDWERK_STEP_TITLES;
    return this._method === 'upload'
      ? ProjectDashboard.UPLOAD_STEP_TITLES
      : ProjectDashboard.STEP_TITLES;
  }

  /** Stap 1 is de methodekeuze; de projectnaam hoort bij stap 2. */
  get _naamStap() {
    return 2;
  }

  _nextStep() {
    // Vanaf stap 2 (basisgegevens) mag je pas verder met een projectnaam.
    if (this._step === this._naamStap && !this.projectNameValue?.trim()) {
      this.createError = 'Projectnaam is verplicht';
      return;
    }
    this.createError = null;
    this._step = Math.min(this._step + 1, this._lastStep);
  }

  _prevStep() {
    this.createError = null;
    this._step = Math.max(this._step - 1, 1);
  }

  _goToStep(n) {
    // Terugspringen mag altijd; vooruit alleen als de naam is ingevuld. Naar de keuzestap
    // terug mag ook: dan kies je opnieuw hoe je dit project maakt.
    if (n > this._step && n > this._naamStap && !this.projectNameValue?.trim()) {
      this.createError = 'Projectnaam is verplicht';
      return;
    }
    this.createError = null;
    this._step = Math.min(Math.max(n, 1), this._lastStep);
  }

  /**
   * Het uploadvenster: slepen-en-neerzetten, of klikken om je bestandsbeheer te openen.
   *
   * Eén vlak dat allebei doet. Een aparte knop naast het sleepvlak is een tweede manier om
   * hetzelfde te bereiken, en dan moet je kiezen voordat je iets kunt.
   */
  _renderUploadStep() {
    return html`
          <p style="margin:0 0 16px; color:var(--mv-text-sub,#6c757d); line-height:1.5;">
            Sleep je export hierheen, of klik om er een te kiezen. GeoVoice herkent het formaat zelf.
          </p>

          <label
            @dragover=${(e) => { e.preventDefault(); this._dragOver = true; }}
            @dragleave=${() => { this._dragOver = false; }}
            @drop=${this._onBestandDrop}
            style="display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px;
                   padding:34px 20px; text-align:center; cursor:pointer;
                   border:2px dashed ${this._dragOver ? 'var(--mv-primary)' : 'var(--mv-border)'};
                   border-radius:14px; background:${this._dragOver ? 'var(--mv-primary-light,#f4faf2)' : 'var(--mv-surface-alt)'};">
            <span style="color:var(--mv-primary);">${unsafeSVG(lucideIcon(FileUp, 30))}</span>
            <span style="font-weight:700; color:var(--mv-text);">
              ${this._uploadBestand ? this._uploadBestand.name : 'Sleep je bestand hierheen'}
            </span>
            <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
              ${this._uploadBestand ? 'Klik om een ander bestand te kiezen' : 'of klik om te bladeren — TerraIndex (.ti)'}
            </span>
            <input type="file" accept=".ti" style="display:none"
              @change=${(e) => this._neemBestand(e.target.files?.[0] || null)}>
          </label>

          ${this._uploadFout ? html`<div class="modal-error" style="margin-top:14px;">${this._uploadFout}</div>` : ''}
    `;
  }

  /** Stap 2 bij 'Vanuit veldwerkopdracht': het formulier kiezen. */
  _renderVeldwerkStep() {
    const f = this._voBestand;
    return html`
      <p style="margin:0 0 16px; color:var(--mv-text-sub,#6c757d); line-height:1.5;">
        Upload de veldwerkopdracht van de projectleider. GeoVoice maakt het project aan, leest het formulier
        in en opent daarna Vooronderzoek → Veldwerkopdracht. Daar controleer en vul je de rest aan.
      </p>
      <label
        @dragover=${(e) => { e.preventDefault(); this._dragOver = true; }}
        @dragleave=${() => { this._dragOver = false; }}
        @drop=${(e) => { e.preventDefault(); this._dragOver = false; if (!this._voBezig) { this._voBestand = e.dataTransfer?.files?.[0] || null; this.createError = null; } }}
        style="display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px;
               padding:34px 20px; text-align:center; cursor:${this._voBezig ? 'default' : 'pointer'};
               border:2px dashed ${this._dragOver ? 'var(--mv-primary)' : 'var(--mv-border)'};
               border-radius:14px; background:${this._dragOver ? 'var(--mv-primary-light,#f4faf2)' : 'var(--mv-surface-alt)'};">
        <span style="color:var(--mv-primary);">${unsafeSVG(lucideIcon(ClipboardList, 30))}</span>
        <span style="font-weight:700; color:var(--mv-text);">${this._voBezig || (f ? f.name : 'Sleep de veldwerkopdracht hierheen')}</span>
        <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
          ${this._voBezig ? 'Even geduld; dit duurt ongeveer 10 seconden.'
            : f ? 'Klik om een ander bestand te kiezen' : 'of klik om te bladeren — Excel, PDF of Word'}
        </span>
        <input type="file" accept=${FIELD_ORDER_ACCEPT} style="display:none" ?disabled=${!!this._voBezig}
          @change=${(e) => { this._voBestand = e.target.files?.[0] || null; this.createError = null; e.target.value = ''; }}>
      </label>`;
  }

  /**
   * Project aanmaken vanuit een veldwerkopdracht: het formulier lezen, een project aanmaken, de
   * opdracht laten herkennen (zelfde route als in het tabblad Veldwerkopdracht), projectnaam en
   * -nummer uit de opdracht overnemen, en naar Vooronderzoek → Veldwerkopdracht.
   */
  async _createFromFieldOrder() {
    const file = this._voBestand;
    if (!file) return;
    this.createError = null;
    let projectId = null;
    try {
      this._voBezig = 'Formulier lezen…';
      let { text } = await fieldOrderDocumentToText(file);
      // Een scan of foto: eerst vragen (vóór het project bestaat), herkennen kan pas met een project.
      const isScan = teWeinigTekst(text);
      if (isScan) {
        if (!kanScanZijn(file)) throw new Error('Er staat (bijna) geen tekst in dit bestand.');
        this._voBezig = null;
        if (!await confirmDialog(SCAN_VRAAG(file.name))) return;
      }
      this._voBezig = 'Project aanmaken…';
      const naam = file.name.replace(/[.][^.]+$/, '');
      const res = await api.createProject({
        name: naam, soil_standard: DEFAULT_SOIL_STANDARD, timezone: DEFAULT_TIMEZONE,
        coordinate_system: DEFAULT_COORDINATE_SYSTEM, height_datum: DEFAULT_HEIGHT_DATUM,
      });
      projectId = res?.projectId;
      if (!projectId) throw new Error('Het project kon niet aangemaakt worden.');
      let melding = null;
      try {
        if (isScan) {
          const scan = await tekstUitScan(projectId, file, (t) => { this._voBezig = t; });
          // Eerst laten zien wat er gelezen is; pas na akkoord wordt het een veldwerkopdracht.
          this._voBezig = 'Herkende tekst controleren…';
          text = await controleerScan({ text: scan.text, paginas: scan.paginas, bestandsnaam: file.name });
          if (text == null) throw Object.assign(new Error('geannuleerd'), { geannuleerd: true });
        }
        this._voBezig = 'Opdracht herkennen…';
        const r = await api.extractFieldOrder(projectId, file, text);
        const p = r?.fieldOrder?.data?.project || {};
        const update = {};
        if (p.name?.trim()) update.name = p.name.trim();
        if (p.number?.trim()) update.code = p.number.trim();
        if (Object.keys(update).length) await api.updateProject(projectId, update);
      } catch (e) {
        // Het project bestaat al; het inlezen kan in het tabblad opnieuw.
        melding = e.geannuleerd
          ? 'Het project is aangemaakt. De scan is niet verwerkt; je kunt het formulier opnieuw inlezen in Veldwerkopdracht.'
          : `Het project is aangemaakt, maar het formulier inlezen lukte niet: ${e.message}. Probeer het opnieuw in Veldwerkopdracht.`;
      }
      this.closeCreateModal();
      this.dispatchEvent(new CustomEvent('project-created'));
      window.history.pushState({}, '', `/projects/${projectId}/vooronderzoek`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      if (melding) alertDialog({ message: melding });
    } catch (e) {
      this.createError = projectId
        ? `Het project is aangemaakt, maar daarna ging iets mis: ${e.message}`
        : `Fout bij aanmaken: ${e.message}`;
    } finally {
      this._voBezig = null;
    }
  }

  _onBestandDrop(e) {
    e.preventDefault();
    this._dragOver = false;
    this._neemBestand(e.dataTransfer?.files?.[0] || null);
  }

  /** Een gekozen bestand aannemen; het formaat bepaalt de backend, niet dit scherm. */
  _neemBestand(file) {
    this._uploadFout = null;
    this._uploadBestand = file;
  }

  /**
   * Het bestand inlezen en doorgaan naar het resultaat.
   *
   * Het inlezen zelf zit nog niet in de backend: die kan TerraIndex en SIKB0101 alleen
   * uitschrijven. Tot die er is, komt er één regel in het log die dat zegt. De vorm van het
   * log staat al wel vast, zodat de echte uitkomst er straks zo in past.
   */
  async _startImport() {
    // Geen bestand: dat is geen reden om de knop uit te schakelen (dan staat hij bleek naast
    // een 'Vorige' die wel gewoon groen is), maar om te zeggen wat er ontbreekt.
    if (!this._uploadBestand) {
      this._uploadFout = 'Kies eerst een bestand om te importeren.';
      return;
    }
    this._importBezig = true;
    this._uploadFout = null;
    try {
      const result = await api.previewTerraIndexImport(this._uploadBestand);
      this._importLog = [
        { level: 'ok', text: `Bestand gelezen: ${this._uploadBestand.name}` },
        ...(result.log || []),
      ];
      this._step = 3;
    } catch (error) {
      // De fout hoort bij de bestandskeuze en niet bij het resultaat, dus we blijven op stap 2
      // staan: dan kun je meteen een ander bestand kiezen zonder terug te navigeren.
      this._uploadFout = error.message || 'Het bestand kon niet gelezen worden.';
    } finally {
      this._importBezig = false;
    }
  }

  /** Het importresultaat: per regel wat er gelukt is, en wat niet. */
  _renderImportLog() {
    const color = { ok: 'var(--mv-primary)', warning: '#B45309', error: 'var(--mv-danger)' };
    return html`
      <p style="margin:0 0 14px; color:var(--mv-text-sub,#6c757d); line-height:1.5;">
        Dit is wat er uit je bestand is gehaald. Klopt het, maak dan het project aan.
      </p>
      <div style="max-height:320px; overflow:auto; border:1px solid var(--mv-border); border-radius:10px;
                  background:var(--mv-surface-alt); padding:10px 12px;">
        ${(this._importLog || []).map(line => html`
          <div style="display:flex; gap:8px; padding:5px 0; font-size:0.85rem; line-height:1.45;
                      border-bottom:1px solid var(--mv-border-soft);">
            <span style="color:${color[line.level] || 'var(--mv-text-muted)'}; font-weight:700; flex:none;">
              ${line.level === 'ok' ? '✓' : line.level === 'warning' ? '!' : '×'}
            </span>
            <span style="color:var(--mv-text);">${line.text}</span>
          </div>`)}
        ${!(this._importLog || []).length
          ? html`<div style="color:var(--mv-text-sub,#6c757d); font-size:0.85rem;">Geen meldingen.</div>` : ''}
      </div>
    `;
  }

  /** Methodekeuze: handmatig invullen, of een export uploaden. */
  _kiesMethode(methode) {
    this.createError = null;
    this._method = methode;
    if (methode === 'upload') {
      this._step = 2;
      this._uploadOpen = true;
      return;
    }
    if (methode === 'veldwerkopdracht') {
      this._step = 2;
      return;
    }
    this._step = 2;
  }

  async _onSoilStandardChange(e) {
    const next = e.target.value;
    // Wisselen van standaard raakt de codering van lagen die er al zijn, dus daar hoort een
    // bevestiging bij. Niet langer omdat 5104 verouderd zou zijn -- dat is nu de default.
    if (next === 'nen5104' && this.soilStandardValue !== 'nen5104') {
      const ok = await confirmDialog({
        title: 'Naar NEN 5104 wisselen?',
        message: 'De standaard bepaalt hoe grondsoorten en bijmengingen gecodeerd worden. Lagen die al beschreven zijn, worden daarna anders gelezen. Weet je zeker dat je wilt wisselen?',
        confirmLabel: 'Ja, NEN 5104',
        cancelLabel: 'Annuleren',
        danger: true,
      });
      if (!ok) {
        // Terugdraaien naar de huidige waarde.
        e.target.value = this.soilStandardValue || DEFAULT_SOIL_STANDARD;
        return;
      }
    }
    this.soilStandardValue = next;
  }

  /**
   * Het vinkje voor de korrelverdeling. Eén render voor de wizard én het bewerkformulier.
   *
   * De inline resets zijn nodig, niet mooi: `.form-group input` in project-dashboard.css zet
   * elk invoerveld op 100 % breed met flinke padding, en `.form-group label` zet de tekst in
   * kapitalen. Bedoeld voor tekstvelden; een selectievakje wordt er een leeg vlak van met de
   * uitleg ernaast buiten beeld. Inline wint van de stylesheet, en dat is hier de kortste
   * eerlijke weg -- een klasse toevoegen zou dezelfde uitzondering zijn, maar verder weg.
   */
  _korrelverdelingVeld() {
    return html`
      <div class="form-group">
        <label class="form-label" style="display:flex; align-items:center; gap:6px;">
          Korrelverdeling
          ${infoTip(GRAIN_SIZE_TIP)}
        </label>
        <label style="display:flex; align-items:center; gap:10px; cursor:pointer; padding:10px;
                      margin:0; text-transform:none; letter-spacing:normal; font-weight:400;
                      border:1px solid var(--mv-border); border-radius:6px; background:var(--mv-surface-alt,#F3F4F6);">
          <input type="checkbox"
            style="width:16px; height:16px; min-width:16px; padding:0; margin:0; flex:none; accent-color:var(--mv-primary);"
            .checked=${!!this.grainSizeInputValue}
            @change=${(e) => { this.grainSizeInputValue = e.target.checked; }}>
          <span style="font-size:0.85rem; color:var(--mv-text); line-height:1.45;">
            Mogelijkheid om percentages op te geven
            <span style="display:block; color:var(--mv-text-muted); font-size:0.8rem; font-weight:400;">
              Per laag optioneel: lutum, leem, grind, silt, organische stof en M50. Vul je ze in, dan volgt de grondsoort daaruit.
            </span>
          </span>
        </label>
      </div>
    `;
  }

  _onTemplateChange(e) {
    const val = e.target.value;
    if (val === '__new__') {
      // De sjablonen-beheerpagina bouwen we hierna; voor nu alleen het signaal doorgeven
      // en de keuze terugzetten zodat er geen '__new__' als sjabloon blijft staan.
      this.templateValue = '';
      e.target.value = '';
      this.dispatchEvent(new CustomEvent('create-template', { bubbles: true, composed: true }));
      return;
    }
    this.templateValue = val;
    // Project-hoofdstuk van het sjabloon direct toepassen (velden blijven bewerkbaar).
    const pd = this._selectedTemplate?.defaults?.project || {};
    if (pd.coordinate_system) this.coordinateSystemValue = pd.coordinate_system;
    if (pd.height_datum) this.heightDatumValue = pd.height_datum;
    if (pd.soil_standard) this.soilStandardValue = normalizeSoilStandard(pd.soil_standard);
    if (pd.grain_size_input !== undefined) this.grainSizeInputValue = !!pd.grain_size_input;
    if (pd.werksoort) this.werksoortValue = pd.werksoort;
  }

  async saveProject(event) {
    event.preventDefault();

    // Bewerken gebruikt een enkelvoudig formulier (geen stepper) — meteen opslaan.
    // Alleen bij een nieuw project betekent Enter op een tussenstap "Volgende".
    if (!this.projectId && this._step !== this._lastStep) {
      this._nextStep();
      return;
    }

    // Een geïmporteerd project heeft geen ingevuld formulier: het bestand is de invoer. Deze
    // aftakking staat dus vóór de controle op projectnaam, want die zou hier altijd falen.
    if (!this.projectId && this._method === 'upload') {
      await this._createFromUpload();
      return;
    }

    const projectName = this.projectNameValue?.trim();
    const projectCode = this.projectCodeValue?.trim();

    if (!projectName || projectName.length === 0) {
      this.createError = 'Projectnaam is verplicht';
      return;
    }

    this.creating = true;
    this.createError = null;

    try {
      const payload = {
        name: projectName,
        code: projectCode || null,
        parent_id: this.parentProjectId || null,
        instructions: this.instructionsValue || null,
        coordinate_system: this.coordinateSystemValue || DEFAULT_COORDINATE_SYSTEM,
        height_datum: this.heightDatumValue || DEFAULT_HEIGHT_DATUM,
        soil_standard: this.soilStandardValue || DEFAULT_SOIL_STANDARD,
        werksoort: this.werksoortValue || null,
        grain_size_input: this.grainSizeInputValue ? 1 : 0,
        timezone: this.timezoneValue || DEFAULT_TIMEZONE,
        template_id: this.templateValue || null
      };

      if (this.projectId) {
        await api.updateProject(this.projectId, payload);
      } else {
        await api.createProject(payload);
      }

      this.closeCreateModal();
      this.dispatchEvent(new CustomEvent('project-created')); // to trigger refresh
    } catch (error) {
      this.createError = `Fout bij opslaan project: ${error.message}`;
    } finally {
      this.creating = false;
    }
  }

  /**
   * De laatste stap van de uploadroute: het ingelezen bestand omzetten in een echt project.
   *
   * De backend leest hetzelfde bestand nog een keer en schrijft project én boringen weg onder
   * het bedrijf waarin je werkt. We sturen dus het bestand en niet het ingelezen resultaat:
   * dat laatste is onderweg door de browser gegaan en zou aangepast kunnen zijn.
   */
  async _createFromUpload() {
    if (!this._uploadBestand) {
      this.createError = 'Er is geen bestand gekozen.';
      return;
    }

    this.creating = true;
    this.createError = null;

    try {
      await api.createProjectFromTerraIndex(this._uploadBestand);
      this.closeCreateModal();
      this.dispatchEvent(new CustomEvent('project-created'));
    } catch (error) {
      this.createError = `Fout bij importeren: ${error.message}`;
    } finally {
      this.creating = false;
    }
  }

  async deleteProject(project) {
    const confirmed = await confirmDialog({
      title: 'Project verwijderen',
      message: `Weet je zeker dat je project "${project.project_name || project.name}" wilt verwijderen?`,
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await api.deleteProject(project.id);
      this.dispatchEvent(new CustomEvent('project-created')); // refresh
    } catch (e) {
      alertDialog({ message: `Fout bij verwijderen: ${e.message}` });
    }
  }

  selectProject(projectId) {
    this.dispatchEvent(new CustomEvent('project-selected', {
      detail: { projectId }
    }));
  }

  render() {
    return html`
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem;">
        <h1 style="margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Projecten</h1>
        <sl-tooltip content="Nieuw Project" placement="left">
          <button class="create-icon-btn" @click=${() => this.toggleCreateModal()}>
            ${unsafeSVG(lucideIcon(Plus, 24))}
          </button>
        </sl-tooltip>
      </div>

      <div class="search-container" style="display:flex; align-items:center; gap:1rem; margin-bottom:1.5rem;">
        <div class="search-input-wrapper" style="flex:1;">
          <span class="search-icon">
            ${unsafeSVG(lucideIcon(Search, 18))}
          </span>
          <input 
            type="text" 
            class="search-input"
            placeholder="Zoeken op naam of code..." 
            .value=${this.searchQuery}
            @input=${this.handleSearchInput}
          />
        </div>

      </div>

      ${this.filteredProjects.length === 0 ? html`
        <div class="empty-state">
          <div class="project-icon">
                        ${unsafeSVG(lucideIcon(Folder, 20))}
                      </div>
          <h3>Geen projecten gevonden</h3>
          <p>${this.searchQuery ? 'Geen projecten gevonden voor uw zoekopdracht.' : 'Maak uw eerste project aan om te beginnen.'}</p>
        </div>
      ` : html`
        <div class="table-card">
          <mv-table
            .columns=${this.tableColumns}
            .data=${this.filteredProjects}
            searchPlaceholder="Zoek in projecten..."
            @row-click=${(e) => this.selectProject(e.detail.row.id)}
          ></mv-table>
        </div>
      `}

      <!-- Create Project Modal -->
      ${this.showCreateModal ? html`
        <div class="modal-overlay" @click=${(e) => e.target === e.currentTarget ? this.closeCreateModal() : null}>
          ${this.projectId ? this._renderEditForm() : html`
          <!-- Met drie keuzes (ook 'Vanuit veldwerkopdracht') een bredere modal, anders past de rij niet. -->
          <div class="modal" style="max-width:${this._vooronderzoekOk ? 860 : 680}px; width:100%; padding:0; overflow:hidden;">
            <div style="display:flex; align-items:stretch;">
              <!-- Verticale stap-indicator (volledige hoogte, links aansluitend) -->
              <div style="flex:0 0 200px; box-sizing:border-box; display:flex; flex-direction:column; padding:28px 22px; background:linear-gradient(165deg, var(--mv-primary-tint,#e9f5e6) 0%, var(--mv-primary-light,#f4faf2) 50%, var(--mv-surface,#fff) 100%); border-right:1px solid var(--mv-border,#eff2f5);">
                ${this._visibleStepTitles.map((title, i) => {
                  const n = i + 1;
                  const active = this._step === n;
                  const done = this._step > n;
                  const last = i === this._visibleStepTitles.length - 1;
                  const circleStyle = done
                    ? 'background:var(--mv-primary); border:2px solid var(--mv-primary); color:#fff;'
                    : active
                      ? 'background:var(--mv-primary-tint,#e9f5e6); border:2px solid var(--mv-primary); color:var(--mv-primary); box-shadow:0 0 0 4px var(--mv-primary-shadow,rgba(75,157,53,0.2));'
                      : 'background:var(--mv-surface,#fff); border:2px solid var(--mv-border,#e2e4ea); color:var(--mv-text-muted,#A1A5B7);';
                  return html`
                    <button type="button" @click=${() => this._goToStep(n)}
                      style="display:flex; align-items:stretch; gap:12px; background:none; border:none; padding:0; cursor:pointer; text-align:left;">
                      <div style="display:flex; flex-direction:column; align-items:center;">
                        <span style="width:30px; height:30px; flex:0 0 30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:0.82rem; font-weight:700; transition:all 0.15s; ${circleStyle}">${done ? '✓' : n}</span>
                        ${!last ? html`<span style="width:2px; flex:1; min-height:34px; margin:6px 0; background:${done ? 'var(--mv-primary)' : 'var(--mv-border,#e2e4ea)'};"></span>` : ''}
                      </div>
                      <div style="padding-top:2px; padding-bottom:${last ? '0' : '34px'};">
                        <div style="font-size:0.62rem; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--mv-text-muted,#A1A5B7);">Stap ${n}</div>
                        <div style="font-size:0.85rem; font-weight:${active ? '700' : '600'}; line-height:1.25; color:${active || done ? 'var(--mv-text)' : 'var(--mv-text-muted,#A1A5B7)'};">${title}</div>
                      </div>
                    </button>
                  `;
                })}
              </div>

              <div style="flex:1; min-width:0; padding:26px 28px;">
              <h3 style="margin:0 0 18px;">${this.projectId ? 'Project bewerken' : 'Nieuw project'}</h3>
              <form @submit=${this.saveProject}>
              <!-- Stap 1: basisgegevens -->
              <!-- Stap 1: hoe maak je dit project? -->
              ${this._step === 1 ? html`
              <p style="margin:0 0 18px; color:var(--mv-text-sub,#6c757d); line-height:1.5;">
                Begin met een leeg project, importeer een bestaand onderzoek${this._vooronderzoekOk ? ', of begin bij de veldwerkopdracht' : ''}.
              </p>
              <div style="display:grid; grid-template-columns:repeat(${this._vooronderzoekOk ? 3 : 2}, 1fr); gap:14px; margin-bottom:8px;">
                ${[
                  { id: 'handmatig', icon: Pencil, titel: 'Handmatig aanmaken',
                    uitleg: 'Je vult de projectgegevens zelf in.' },
                  ...(this._vooronderzoekOk ? [{ id: 'veldwerkopdracht', icon: ClipboardList, titel: 'Vanuit veldwerkopdracht',
                    uitleg: 'Upload de opdracht; GeoVoice leest de projectgegevens eruit.' }] : []),
                  { id: 'upload', icon: Upload, titel: 'Bestand uploaden',
                    uitleg: 'TerraIndex (.ti) — het formaat wordt herkend.' },
                ].map(keuze => html`
                  <button type="button" @click=${() => this._kiesMethode(keuze.id)}
                    style="display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; min-width:0;
                           aspect-ratio:1; padding:18px; text-align:center; cursor:pointer;
                           border:1.5px solid ${this._method === keuze.id ? 'var(--mv-primary)' : 'var(--mv-border)'};
                           border-radius:14px; background:${this._method === keuze.id ? 'var(--mv-primary-light,#f4faf2)' : 'var(--mv-surface,#fff)'};
                           font-family:inherit;">
                    <span style="width:46px; height:46px; border-radius:50%; display:flex; align-items:center; justify-content:center;
                                 background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary);">
                      ${unsafeSVG(lucideIcon(keuze.icon, 22))}
                    </span>
                    <span style="font-size:0.95rem; font-weight:700; color:var(--mv-text);">${keuze.titel}</span>
                    <span style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); line-height:1.35;">${keuze.uitleg}</span>
                  </button>
                `)}
              </div>
              ` : ''}

              <!-- Stap 2 bij uploaden: het bestand. Binnen ditzelfde venster, niet in een
                   tweede popup bovenop de eerste. -->
              ${this._step === 2 && this._method === 'upload' ? this._renderUploadStep() : ''}
              ${this._step === 2 && this._method === 'veldwerkopdracht' ? this._renderVeldwerkStep() : ''}

              <!-- Stap 3 bij uploaden: wat de import ervan maakte. Je ziet eerst wat er
                   binnenkwam en wat er is overgeslagen, en pas daarna maak je het project aan. -->
              ${this._step === 3 && this._method === 'upload' ? this._renderImportLog() : ''}

              <!-- Stap 2 bij handmatig: basisgegevens -->
              ${this._step === 2 && this._method !== 'upload' && this._method !== 'veldwerkopdracht' ? html`
              <div class="form-group">
                <label for="project_name">Projectnaam *</label>
                <input
                  type="text"
                  id="project_name"
                  name="project_name"
                  .value=${this.projectNameValue}
                  placeholder="Voer projectnaam in..."
                  required
                  @input=${this.handleNameInput}
                />
              </div>
              <div class="form-group">
                <label for="project_code">Projectcode</label>
                <input
                  type="text"
                  id="project_code"
                  name="project_code"
                  .value=${this.projectCodeValue}
                  placeholder="Bijv. PRJ-2024-001"
                  @input=${this.handleCodeInput}
                />
                <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">
                  Automatisch voorgesteld — pas aan of laat leeg voor geen code.
                </div>
              </div>

              <div class="form-group">
                <label for="parent_id">Hoofdproject (optioneel)</label>
                <select
                  id="parent_id"
                  name="parent_id"
                  .value=${this.parentProjectId}
                  @change=${(e) => this.parentProjectId = e.target.value}
                >
                  <option value="">-- Geen (dit is een hoofdproject) --</option>
                  ${this.projects.filter(p => !p.parent_id && p.id !== this.projectId).map(p => html`
                    <option value="${p.id}">${p.project_name || p.name || p.id}</option>
                  `)}
                </select>
              </div>

              <div class="form-group">
                <label for="template" class="form-label">Sjabloon (optioneel)</label>
                <select id="template" class="form-input"
                  style="width: 100%; border: 1px solid var(--mv-border); border-radius: 6px; padding: 10px; font-family: inherit; font-size: 0.9rem; background: var(--mv-surface,#fff); color: var(--mv-text);"
                  .value=${this.templateValue || ''}
                  @change=${(e) => this._onTemplateChange(e)}>
                  <option value="">— Geen sjabloon —</option>
                  ${(this.templates || []).map((t) => html`<option value=${t.id} ?selected=${t.id === this.templateValue}>${t.name}</option>`)}
                  ${this._canManageTemplates ? html`<option value="__new__">➕ Maak nieuwe sjabloon aan</option>` : ''}
                </select>
                <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); margin-top:4px; line-height:1.5;">
                  Een sjabloon vult automatisch standaardvelden voor je in (die je daarna nog kunt aanpassen).
                </div>
                ${this.projectId && this.templateValue ? html`
                  <div style="margin-top:8px; padding:10px 12px; border:1px solid var(--mv-warning-panel-border,#FDE68A); border-radius:8px; background:var(--mv-warning-panel-bg,#FFFBEB); font-size:0.78rem; color:var(--mv-warning-text-sub,#B45309); line-height:1.5;">
                    <b>Let op:</b> op een bestaand project geldt dit sjabloon alleen voor <b>nieuwe</b> boringen/lagen/potjes/peilbuizen die je hierna aanmaakt. <b>Bestaande</b> boringen worden niet aangepast.
                  </div>` : ''}
              </div>
              ` : ''}

              <!-- Stap 2: standaarden & systemen -->
              ${this._step === 3 ? html`
              <div style="margin:2px 0 12px; font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; color:var(--mv-text-sub,#6c757d);">
                Standaarden &amp; systemen
              </div>

              <div class="form-group">
                <label for="coord_system" class="form-label">Coördinaatsysteem</label>
                <select
                  id="coord_system"
                  class="form-input"
                  style="width: 100%; border: 1px solid var(--mv-border); border-radius: 6px; padding: 10px; font-family: inherit; font-size: 0.9rem; background: var(--mv-surface,#fff); color: var(--mv-text);"
                  .value=${this.coordinateSystemValue || DEFAULT_COORDINATE_SYSTEM}
                  @change=${(e) => this.coordinateSystemValue = e.target.value}
                >
                  ${Object.values(COORDINATE_SYSTEMS).map((s) => html`<option value=${s.id} ?selected=${s.id === (this.coordinateSystemValue || DEFAULT_COORDINATE_SYSTEM)}>${s.label}</option>`)}
                </select>
              </div>

              <div class="form-group">
                <label for="height_datum" class="form-label">Hoogtesysteem</label>
                <select
                  id="height_datum"
                  class="form-input"
                  style="width: 100%; border: 1px solid var(--mv-border); border-radius: 6px; padding: 10px; font-family: inherit; font-size: 0.9rem; background: var(--mv-surface,#fff); color: var(--mv-text);"
                  .value=${this.heightDatumValue || DEFAULT_HEIGHT_DATUM}
                  @change=${(e) => this.heightDatumValue = e.target.value}
                >
                  ${Object.values(HEIGHT_DATUMS).map((s) => html`<option value=${s.id} ?selected=${s.id === (this.heightDatumValue || DEFAULT_HEIGHT_DATUM)}>${s.label}</option>`)}
                </select>
              </div>

              <div class="form-group">
                <label for="soil_standard" class="form-label" style="display:flex; align-items:center; gap:6px;">
                  Standaard boorprofiel
                  ${infoTip(SOIL_STANDARD_TIP)}
                </label>
                <select
                  id="soil_standard"
                  class="form-input"
                  style="width: 100%; border: 1px solid var(--mv-border); border-radius: 6px; padding: 10px; font-family: inherit; font-size: 0.9rem; background: var(--mv-surface,#fff); color: var(--mv-text);"
                  .value=${this.soilStandardValue || DEFAULT_SOIL_STANDARD}
                  @change=${(e) => this._onSoilStandardChange(e)}
                >
                  ${SELECTABLE_SOIL_STANDARDS.map((s) => html`<option value=${s.id} ?selected=${s.id === (this.soilStandardValue || DEFAULT_SOIL_STANDARD)}>${s.label}</option>`)}
                </select>
              </div>

              ${this._korrelverdelingVeld()}
              ${tijdzoneVeld(this.timezoneValue, (tz) => { this.timezoneValue = tz; })}
              ` : ''}

              <!-- Stap 3: onderzoek -->
              ${this._step === 4 ? html`
              <div class="form-group">
                <label for="werksoort" class="form-label" style="display:flex; align-items:center; gap:6px;">
                  Werksoort onderzoek
                  <button type="button" @click=${() => this._werksoortInfoOpen = !this._werksoortInfoOpen}
                    title="Toelichting per werksoort"
                    style="border:none; background:none; cursor:pointer; color:var(--mv-primary,#1c4fb3); font-size:1rem; line-height:1; padding:0;">ⓘ</button>
                </label>
                <select id="werksoort" class="form-input"
                  style="width:100%; border:1px solid var(--mv-border); border-radius:6px; padding:10px; font-family:inherit; font-size:0.9rem; background:var(--mv-surface,#fff); color:var(--mv-text);"
                  .value=${this.werksoortValue || ''}
                  @change=${(e) => this.werksoortValue = e.target.value}>
                  <option value="">— kies (optioneel) —</option>
                  ${nen6693.WERKSOORT.map((w) => html`<option value=${w.code} ?selected=${w.code === this.werksoortValue}>${w.label}</option>`)}
                </select>
                ${this.werksoortValue ? html`
                  <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">
                    NEN 6693-regelset: <strong>${nen6693.WERKSOORT.find(w => w.code === this.werksoortValue)?.nenScope}</strong>
                  </div>` : ''}
                ${this._werksoortInfoOpen ? html`
                  <div style="margin-top:8px; padding:10px 12px; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-bg,#f7f8fa); font-size:0.8rem; color:var(--mv-text); line-height:1.5;">
                    <div style="color:var(--mv-text-sub,#6c757d); margin-bottom:8px;">Bepaalt (volgens NEN 6693) welke velden verplicht zijn. Standaard op projectniveau, per boring te overrulen.</div>
                    ${nen6693.WERKSOORT.map((w) => html`
                      <div style="margin-bottom:4px;"><strong>${w.label}</strong> — <span style="color:var(--mv-text-sub,#6c757d);">${w.short}</span></div>`)}
                  </div>` : ''}
              </div>

              <div class="form-group">
                <label for="instructions" class="form-label">Werkinstructies (optioneel)</label>
                <textarea
                  id="instructions"
                  name="instructions"
                  class="form-input"
                  style="min-height: 100px; width: 100%; border: 1px solid var(--mv-border); border-radius: 6px; padding: 10px; font-family: inherit; font-size: 0.9rem; resize: vertical;"
                  placeholder="Voer instructies in voor de veldwerkers..."
                  .value=${this.instructionsValue}
                  @input=${(e) => this.instructionsValue = e.target.value}
                ></textarea>
              </div>
              ` : ''}

              ${this.createError ? html`<div class="modal-error">${this.createError}</div>` : ''}

              <div class="modal-actions" style="justify-content:space-between;">
                <button type="button" class="btn-cancel" @click=${this.closeCreateModal}>Annuleren</button>
                <div style="display:flex; gap:8px;">
                  ${this._step > 1 ? html`<button type="button" class="btn-cancel" @click=${() => this._prevStep()}>Vorige</button>` : ''}
                  <!-- Bij de methodekeuze is de keuze zelf de doorgang; een knop 'Volgende'
                       zou een derde manier zijn om hetzelfde te doen. -->
                  ${this._step === 1 ? ''
                    : this._method === 'veldwerkopdracht'
                    ? html`<button type="button" class="btn-save" ?disabled=${!this._voBestand || !!this._voBezig}
                        @click=${() => this._createFromFieldOrder()}>${this._voBezig ? 'Bezig…' : 'Project aanmaken'}</button>`
                    : (this._method === 'upload' && this._step === 2)
                    ? html`<button type="button" class="btn-save" ?disabled=${!this._uploadBestand || this._importBezig}
                        @click=${this._startImport}>${this._importBezig ? 'Bezig met importeren...' : 'Importeren'}</button>`
                    : this._step < this._lastStep
                    ? html`<button type="button" class="btn-save" @click=${() => this._nextStep()}>Volgende</button>`
                    : html`<button type="submit" class="btn-save" ?disabled=${this.creating}>${this.creating ? 'Opslaan...' : (this.projectId ? 'Opslaan' : 'Project aanmaken')}</button>`}
                </div>
              </div>
            </form>
            </div>
            </div>
          </div>`}
        </div>
      ` : ''}

    `;
  }

  /**
   * Enkelvoudig bewerk-formulier (geen stepper). De stepper is alleen voor het
   * aanmaken van een nieuw project; bij bewerken tonen we één overzichtelijk
   * formulier met alle velden onder elkaar.
   */
  _renderEditForm() {
    return html`
      <div class="modal project-edit-form" style="max-height:85vh; overflow-y:auto;">
        <h3 style="margin:0 0 1.5rem; color:var(--mv-text);">Project bewerken</h3>
        <form @submit=${this.saveProject}>
          ${projectVelden({
            waarden: {
              name: this.projectNameValue,
              code: this.projectCodeValue,
              parentId: this.parentProjectId,
              coordinateSystem: this.coordinateSystemValue,
              heightDatum: this.heightDatumValue,
              soilStandard: this.soilStandardValue,
              grainSizeInput: this.grainSizeInputValue,
              timezone: this.timezoneValue,
              werksoort: this.werksoortValue,
              templateId: this.templateValue,
              instructions: this.instructionsValue,
            },
            zet: (k, v) => this._zetProjectVeld(k, v),
            // Alleen hier: dit is de enige plek waar je een project onder een ander kunt hangen.
            hoofdprojecten: (this.projects || []).filter((p) => !p.parent_id && p.id !== this.projectId),
            sjablonen: this.templates || [],
            sjabloonInitieel: this._templateInitial,
            sjabloonAanmaken: this._canManageTemplates,
            opSjabloon: (e) => this._onTemplateChange(e),
            opStandaard: (e) => this._onSoilStandardChange(e),
          })}

          ${this.createError ? html`<div class="modal-error">${this.createError}</div>` : ''}

          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click=${this.closeCreateModal}>Annuleren</button>
            <button type="submit" class="btn-save" ?disabled=${this.creating}>${this.creating ? 'Opslaan...' : 'Opslaan'}</button>
          </div>
        </form>
      </div>
    `;
  }

  /**
   * Eén veld uit het gedeelde projectformulier terug in de eigen toestand.
   *
   * De namen in dat formulier zijn de domeinnamen; hier staan ze historisch anders. Deze tabel
   * is de vertaling, zodat opslaan (`saveProject`) ongewijzigd blijft werken.
   */
  _zetProjectVeld(sleutel, waarde) {
    const naar = {
      name: 'projectNameValue', code: 'projectCodeValue', parentId: 'parentProjectId',
      coordinateSystem: 'coordinateSystemValue', heightDatum: 'heightDatumValue',
      soilStandard: 'soilStandardValue', grainSizeInput: 'grainSizeInputValue',
      timezone: 'timezoneValue', werksoort: 'werksoortValue', templateId: 'templateValue',
      instructions: 'instructionsValue',
    }[sleutel];
    if (naar) this[naar] = waarde;
  }
}

customElements.define('project-dashboard', ProjectDashboard);
